import React, { useState, useEffect } from "react";
import {
  Button, Dialog, DialogContent, IconButton, Typography,
  Stack, FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, OutlinedInput, Chip, Box,
} from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import styled from "styled-components";
import OpaqueLoading from "../opaqueLoading/opaqueLoading";

const StyledDialogContent = styled(DialogContent)`
  padding: 16px;
`;

const ModalHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const OlympiadClassSubjectMappingModal = ({ onClose, fetchData, propData }) => {
  const [loading, setLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);

  // ── Fetch classes and subjects on mount ──
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        setLoading(true);
        const [classRes, subjectRes] = await Promise.all([
          window.Platform.database.getAllOlympiadClasses(),
          window.Platform.database.getAllOlympiadSubjects(),
        ]);
        if (classRes?.status) setClassOptions(classRes.data || []);
        if (subjectRes?.status) setSubjectOptions(subjectRes.data || []);
      } catch (err) {
        window.NotificationUtils.showError("Failed to load dropdown data");
      } finally {
        setLoading(false);
      }
    };
    fetchDropdowns();
  }, []);

  // ── Prefill when editing ──
useEffect(() => {
  if (propData && subjectOptions.length > 0) {
    setSelectedClassId(propData.class_id || "");
    setSelectedSubjectIds(propData.subject_ids || []);
  }
}, [propData, subjectOptions]); 

  const submitHandler = async () => {
    if (!selectedClassId) {
      window.NotificationUtils.showError("Please select a class");
      return;
    }
    if (!selectedSubjectIds.length) {
      window.NotificationUtils.showError("Please select at least one subject");
      return;
    }

    setLoading(true);
    try {
      if (propData) {
        await window.Platform.database.updateClassSubjectMapping({
          id: propData.id,
          class_id: selectedClassId,
          subject_ids: selectedSubjectIds,
        });
        window.NotificationUtils.showSuccess("Mapping updated successfully");
      } else {
        const mappings = selectedSubjectIds.map((subject_id) => ({
          class_id: selectedClassId,
          subject_id,
        }));
        await window.Platform.database.addClassSubjectMapping({ mappings });
        window.NotificationUtils.showSuccess("Mapping saved successfully");
      }
      fetchData();
      onClose();
    } catch (err) {
      window.NotificationUtils.showError(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog disableEnforceFocus maxWidth="sm" fullWidth open>
      <StyledDialogContent>
        <ModalHeaderContainer>
          <Typography variant="h5">
            {propData ? "Edit Class-Subject Mapping" : "Add Class-Subject Mapping"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </ModalHeaderContainer>

        {loading && <OpaqueLoading />}

        <Stack gap={3} padding={2}>

          {/* ── Class Dropdown ── */}
          <FormControl fullWidth>
            <InputLabel>Class *</InputLabel>
            <Select
              value={selectedClassId}
              label="Class *"
              onChange={(e) => setSelectedClassId(e.target.value)}
            >
              {classOptions.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.class_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ── Subject Multi-Select ── */}
          <FormControl fullWidth>
            <InputLabel>Subjects *</InputLabel>
            <Select
              multiple
              value={selectedSubjectIds}
              label="Subjects *"
              input={<OutlinedInput label="Subjects *" />}
              onChange={(e) => setSelectedSubjectIds(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((id) => {
                    const subj = subjectOptions.find((s) => s.id === id);
                    return <Chip key={id} label={subj?.subject_name || id} size="small" />;
                  })}
                </Box>
              )}
            >
              {subjectOptions.map((subj) => (
                <MenuItem key={subj.id} value={subj.id}>
                  <Checkbox checked={selectedSubjectIds.includes(subj.id)} />
                  <ListItemText primary={subj.subject_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" justifyContent="center">
            <Button
              color="primary"
              variant="contained"
              onClick={submitHandler}
              disabled={!selectedClassId || !selectedSubjectIds.length}
            >
              {propData ? "Update" : "Create"}
            </Button>
          </Box>

        </Stack>
      </StyledDialogContent>
    </Dialog>
  );
};

export default OlympiadClassSubjectMappingModal;